import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "count", "submitBtn", "submitBtnMobile", "summary", "cartList", "cartEmpty", "cartCount", "templateSelect", "saveStatus"]
  static values = {
    preselect: { type: Object, default: {} },
    draftId: { type: Number, default: 0 },
    createUrl: { type: String, default: "" },
    editUrlTemplate: { type: String, default: "" }
  }

  connect() {
    this.nextPosition = 0
    this._autoSaveTimer = null
    this._autoSaveInFlight = false
    this._pendingAutoSave = false
    this._preselecting = false
    this.updateCount()

    // Pre-select items if data is provided (edit/template prefill/assignment→log)
    if (Object.keys(this.preselectValue).length > 0) {
      // Defer to allow all tag-toggle controllers to connect first
      this._preselecting = true
      requestAnimationFrame(() => {
        this._preselectItems()
        this._preselecting = false
      })
    }
  }

  loadTemplate(event) {
    const templateId = event.target.value
    const url = new URL(window.location.href)
    if (templateId) {
      url.searchParams.set("template_id", templateId)
    } else {
      url.searchParams.delete("template_id")
    }
    window.Turbo.visit(url.toString())
  }

  updateCount() {
    const form = this.formTarget
    const asanaInputs = form.querySelectorAll('input[name^="selected_asanas"]')
    const exerciseInputs = form.querySelectorAll('input[name^="selected_exercises"]')

    // Each selected item has 3 hidden inputs (reps, sets, position)
    const selectedCount = (asanaInputs.length + exerciseInputs.length) / 3

    // Update all count targets (there may be multiple: sidebar + mobile)
    this.countTargets.forEach(el => el.textContent = selectedCount)

    // Update all cart count targets
    if (this.hasCartCountTarget) {
      this.cartCountTargets.forEach(el => el.textContent = selectedCount)
    }

    // Update submit buttons
    this.submitBtnTargets.forEach(btn => btn.disabled = selectedCount === 0)
    if (this.hasSubmitBtnMobileTarget) {
      this.submitBtnMobileTargets.forEach(btn => btn.disabled = selectedCount === 0)
    }

    // Update empty state
    if (this.hasCartEmptyTarget) {
      this.cartEmptyTargets.forEach(el => {
        el.classList.toggle("hidden", selectedCount > 0)
      })
    }
    if (this.hasCartListTarget) {
      this.cartListTargets.forEach(el => {
        el.classList.toggle("hidden", selectedCount === 0)
      })
    }
  }

  syncCart(event) {
    const { active, type, id, name, color, reps, sets, supportsReps, supportsSets } = event.detail

    if (active) {
      // Set position on the hidden input for this item
      const paramType = type === "asana" ? "asanas" : "exercises"
      const posInput = this.formTarget.querySelector(`input[name="selected_${paramType}[${id}][position]"]`)
      if (posInput) posInput.value = this.nextPosition++

      this._addToCart({ type, id, name, color, reps, sets, supportsReps, supportsSets })
    } else {
      this._removeFromCart(type, id)
    }

    this.scheduleAutoSave()
  }

  updateCartItem(event) {
    const { type, id, reps, sets } = event.detail
    const cartItemId = `cart-item-${type}-${id}`

    // Update in all cart lists
    this.cartListTargets.forEach(cartList => {
      const item = cartList.querySelector(`[data-cart-item-id="${cartItemId}"]`)
      if (!item) return

      const repsVal = item.querySelector("[data-cart-reps-value]")
      const setsVal = item.querySelector("[data-cart-sets-value]")
      if (repsVal) repsVal.textContent = reps
      if (setsVal) setsVal.textContent = sets
    })

    this.scheduleAutoSave()
  }

  cartAdjust(event) {
    const btn = event.currentTarget
    const type = btn.dataset.itemType
    const id = parseInt(btn.dataset.itemId)
    const method = btn.dataset.adjust

    const selector = `[data-tag-toggle-type-value="${type}"][data-tag-toggle-id-value="${id}"]`
    const tagElement = this.element.querySelector(selector)
    if (!tagElement) return

    const controller = this.application.getControllerForElementAndIdentifier(tagElement, "tag-toggle")
    if (!controller) return

    controller[method]()
  }

  reorder(event) {
    // Read new order from the cart list that was just dragged
    const sourceCart = event.target
    const items = sourceCart.querySelectorAll("[data-cart-item-id]")

    // Update position hidden inputs in the form to match new DOM order
    items.forEach((item, index) => {
      const cartItemId = item.dataset.cartItemId
      // cartItemId format: "cart-item-{type}-{id}"
      const match = cartItemId.match(/^cart-item-(asana|exercise)-(\d+)$/)
      if (!match) return

      const [, type, id] = match
      const paramType = type === "asana" ? "asanas" : "exercises"
      const posInput = this.formTarget.querySelector(`input[name="selected_${paramType}[${id}][position]"]`)
      if (posInput) posInput.value = index
    })

    // Sync the other cart list(s) to match the same order
    this.cartListTargets.forEach(cartList => {
      if (cartList === sourceCart) return
      const orderedIds = Array.from(items).map(i => i.dataset.cartItemId)
      orderedIds.forEach(cartItemId => {
        const el = cartList.querySelector(`[data-cart-item-id="${cartItemId}"]`)
        if (el) cartList.appendChild(el)
      })
    })

    // Update nextPosition so new items get appended after existing ones
    this.nextPosition = items.length

    this.scheduleAutoSave()
  }

  removeFromCart(event) {
    const type = event.currentTarget.dataset.itemType
    const id = parseInt(event.currentTarget.dataset.itemId)

    // Find the corresponding tag-toggle controller and deactivate it
    const selector = `[data-tag-toggle-type-value="${type}"][data-tag-toggle-id-value="${id}"]`
    const tagElement = this.element.querySelector(selector)
    if (tagElement) {
      const controller = this.application.getControllerForElementAndIdentifier(tagElement, "tag-toggle")
      if (controller) {
        controller.deactivateFromCart()
      }
    }
  }

  // ===== Survey Submit =====

  submitWithSurvey(event) {
    event.preventDefault()

    // Find the survey-modal controller on the page
    const surveyEl = document.querySelector("[data-controller~='survey-modal']")
    if (surveyEl) {
      const surveyController = this.application.getControllerForElementAndIdentifier(
        surveyEl, "survey-modal"
      )
      if (surveyController) {
        surveyController.open()
        return
      }
    }

    // Fallback: submit directly if no survey modal found
    this.formTarget.requestSubmit()
  }

  // ===== Auto-Save =====

  scheduleAutoSave() {
    // Don't auto-save during preselection (edit page load)
    if (this._preselecting) return
    // Need either a createUrl (new) or a draftId (edit) to auto-save
    if (!this.createUrlValue && this.draftIdValue === 0) return

    clearTimeout(this._autoSaveTimer)
    this._autoSaveTimer = setTimeout(() => this._performAutoSave(), 800)
  }

  async _performAutoSave() {
    if (this._autoSaveInFlight) {
      this._pendingAutoSave = true
      return
    }
    this._autoSaveInFlight = true
    this._showSaveStatus("saving")

    const formData = new FormData(this.formTarget)
    formData.append("auto_save", "true")

    const isNew = this.draftIdValue === 0
    let url

    if (isNew) {
      url = this.createUrlValue
    } else {
      url = this.editUrlTemplateValue.replace(":id", this.draftIdValue)
      formData.append("_method", "patch")
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        if (isNew && data.id) {
          this.draftIdValue = data.id
          // Transform form from POST (create) to PATCH (update)
          this.formTarget.action = this.editUrlTemplateValue.replace(":id", data.id)
          const methodInput = document.createElement("input")
          methodInput.type = "hidden"
          methodInput.name = "_method"
          methodInput.value = "patch"
          this.formTarget.appendChild(methodInput)
        }
        this._showSaveStatus("saved")
      } else {
        this._showSaveStatus("error")
      }
    } catch (e) {
      this._showSaveStatus("error")
    } finally {
      this._autoSaveInFlight = false
      if (this._pendingAutoSave) {
        this._pendingAutoSave = false
        this.scheduleAutoSave()
      }
    }
  }

  _showSaveStatus(state) {
    if (!this.hasSaveStatusTarget) return
    const el = this.saveStatusTarget

    switch (state) {
      case "saving":
        el.textContent = "Saving..."
        el.className = "text-xs text-gray-400 animate-pulse"
        break
      case "saved":
        el.textContent = "Draft saved"
        el.className = "text-xs text-green-500"
        clearTimeout(this._saveStatusTimer)
        this._saveStatusTimer = setTimeout(() => {
          el.textContent = ""
          el.className = ""
        }, 2000)
        break
      case "error":
        el.textContent = "Save failed"
        el.className = "text-xs text-red-500"
        break
    }
  }

  // ===== Private =====

  _addToCart({ type, id, name, color, reps, sets, supportsReps, supportsSets }) {
    const cartItemId = `cart-item-${type}-${id}`
    const colorMap = {
      indigo: { bg: "bg-indigo-50", text: "text-indigo-600", badge: "bg-indigo-100 text-indigo-600" },
      purple: { bg: "bg-purple-50", text: "text-purple-600", badge: "bg-purple-100 text-purple-600" },
      emerald: { bg: "bg-emerald-50", text: "text-emerald-600", badge: "bg-emerald-100 text-emerald-600" }
    }
    const colors = colorMap[color] || colorMap.indigo

    const adjBtn = (label, method) => `
      <button type="button"
              class="w-5 h-5 rounded-md bg-gray-100 text-gray-500 text-xs flex items-center justify-center hover:bg-gray-200 transition-colors"
              data-action="practice-log#cartAdjust"
              data-item-type="${type}" data-item-id="${id}" data-adjust="${method}">${label}</button>`

    let repsControl = ""
    if (supportsReps) {
      repsControl = `
        <div class="flex items-center gap-0.5">
          ${adjBtn("&minus;", "decrementReps")}
          <span data-cart-reps-value class="text-xs font-medium w-4 text-center text-gray-500">${reps}</span>
          ${adjBtn("&plus;", "incrementReps")}
          <span class="text-[10px] text-gray-400 ml-0.5">reps</span>
        </div>`
    }

    let setsControl = ""
    if (supportsSets) {
      setsControl = `
        <div class="flex items-center gap-0.5">
          ${adjBtn("&minus;", "decrementSets")}
          <span data-cart-sets-value class="text-xs font-medium w-4 text-center text-gray-500">${sets}</span>
          ${adjBtn("&plus;", "incrementSets")}
          <span class="text-[10px] text-gray-400 ml-0.5">sets</span>
        </div>`
    }

    const controls = (repsControl || setsControl)
      ? `<div class="flex items-center gap-2">${repsControl}${setsControl}</div>`
      : ""

    const html = `
      <div data-cart-item-id="${cartItemId}" class="cart-item-enter flex items-center justify-between py-2 px-3 rounded-xl ${colors.bg} group">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span data-drag-handle class="shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-400 touch-none">
            <svg class="w-3.5 h-4" viewBox="0 0 14 16" fill="currentColor">
              <circle cx="4" cy="3" r="1.5"/><circle cx="10" cy="3" r="1.5"/>
              <circle cx="4" cy="8" r="1.5"/><circle cx="10" cy="8" r="1.5"/>
              <circle cx="4" cy="13" r="1.5"/><circle cx="10" cy="13" r="1.5"/>
            </svg>
          </span>
          <span class="text-xs font-medium ${colors.text} truncate">${this._escapeHtml(name)}</span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          ${controls}
          <button type="button"
                  class="shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-gray-300
                         hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                  data-action="practice-log#removeFromCart"
                  data-item-type="${type}"
                  data-item-id="${id}">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
    `

    this.cartListTargets.forEach(cartList => {
      cartList.insertAdjacentHTML("beforeend", html)
    })
  }

  _removeFromCart(type, id) {
    const cartItemId = `cart-item-${type}-${id}`
    this.cartListTargets.forEach(cartList => {
      const item = cartList.querySelector(`[data-cart-item-id="${cartItemId}"]`)
      if (item) {
        item.style.transition = "opacity 0.15s, transform 0.15s"
        item.style.opacity = "0"
        item.style.transform = "translateX(10px)"
        setTimeout(() => item.remove(), 150)
      }
    })
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _preselectItems() {
    const data = this.preselectValue
    for (const [type, items] of Object.entries(data)) {
      for (const [id, attrs] of Object.entries(items)) {
        const selector = `[data-tag-toggle-type-value="${type}"][data-tag-toggle-id-value="${id}"]`
        const tagElement = this.element.querySelector(selector)
        if (tagElement) {
          const controller = this.application.getControllerForElementAndIdentifier(tagElement, "tag-toggle")
          if (controller && !controller.activeValue) {
            if (attrs.reps) controller.repsValue = attrs.reps
            if (attrs.sets) controller.setsValue = attrs.sets
            controller.toggle()
          }
        }
      }
    }
  }
}
